/**
 * Pure helpers that turn the chat SSE stream's tool and model events into a
 * {@link TurnActivity}: the "Working for 12s · Searching contracts" record
 * the chat shows while the agent works, and the collapsible list of steps it
 * leaves behind.
 *
 * Kept free of React imports so it can be unit tested in isolation and
 * reused by {@link ChatSocketProvider}.
 */
import {
  ActivitySegment,
  ActivityStep,
  Message,
  TurnActivity,
} from '@monetize/types/app';
import isArray from 'lodash/isArray';

/** Tool input and output are kept for the step details view and persisted
 * with the conversation, so they are capped to keep local storage small. */
export const ACTIVITY_TEXT_LIMIT = 2000;

/** Agent-to-agent handoffs are plumbing, not work the user cares about. */
const HANDOFF_TOOL_PATTERN = /^transfer_(?:back_)?to_/;

export const isHandoffTool = (toolName: string) =>
  HANDOFF_TOOL_PATTERN.test(toolName);

export const capActivityText = (text: string) =>
  text.length > ACTIVITY_TEXT_LIMIT
    ? `${text.slice(0, ACTIVITY_TEXT_LIMIT)}…`
    : text;

/** A tool result arrives as a string or as `{ type: 'text', text }` blocks. */
export const extractToolOutput = (content: unknown): string | undefined => {
  if (typeof content === 'string') {
    return content;
  }
  if (isArray(content)) {
    const text = content
      .map((block) =>
        typeof block === 'object' &&
        block !== null &&
        typeof (block as { text?: unknown }).text === 'string'
          ? (block as { text: string }).text
          : '',
      )
      .join('');
    return text || undefined;
  }
  return undefined;
};

/**
 * Records returned by a search or query, read before the output is capped
 * (a capped JSON string no longer parses). Searches report the full match
 * count, not just the page they returned.
 */
export const countToolResults = (
  toolName: string | null | undefined,
  output: string | undefined,
): number | undefined => {
  if (
    !output ||
    (toolName !== 'monetizenow_search_objects' && toolName !== 'execute_query')
  ) {
    return undefined;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(output);
  } catch {
    return undefined;
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return undefined;
  }
  const {
    pagination,
    results,
    row_count: rowCount,
  } = parsed as {
    pagination?: { total?: unknown };
    results?: unknown;
    row_count?: unknown;
  };
  if (toolName === 'execute_query') {
    return typeof rowCount === 'number' ? rowCount : undefined;
  }
  if (typeof pagination?.total === 'number') {
    return pagination.total;
  }
  return isArray(results) ? results.length : undefined;
};

export interface ToolUseBlock {
  type: 'tool_use';
  id: string;
  name: string;
}

/** A `token` block announcing a tool call, before its arguments stream in. */
export const isToolUseBlock = (block: unknown): block is ToolUseBlock =>
  typeof block === 'object' &&
  block !== null &&
  (block as { type?: unknown }).type === 'tool_use' &&
  typeof (block as { id?: unknown }).id === 'string' &&
  typeof (block as { name?: unknown }).name === 'string';

export type ActivityAction =
  /** A model call started; the agent is deciding what to do next. */
  | { type: 'thinking' }
  /** A tool call was announced (`tool_use` token) or started (`tool_start`). */
  | {
      type: 'toolCalled';
      id?: string | null;
      toolName: string;
      input?: string;
    }
  | {
      type: 'toolFinished';
      id?: string | null;
      toolName?: string | null;
      output?: string;
      failed?: boolean;
    }
  /** A sub-agent is writing up its findings for the supervisor. */
  | { type: 'drafting' }
  /** Sub-agent text turned out to be narration ahead of a tool call. */
  | { type: 'narrated'; id: string; text: string }
  /** A visible reply started streaming, which ends the current work. */
  | { type: 'replied' }
  /** A dropped connection is resending the prompt; the turn starts over. */
  | { type: 'restarted' }
  | { type: 'finished'; stopped: boolean };

export interface ActivityContext {
  now: number;
  /** Visible replies already in the turn, which places a new segment. */
  replyCount: number;
}

export const createTurnActivity = (now: number): TurnActivity => ({
  startedAt: now,
  status: 'running',
  phase: 'thinking',
  segments: [{ position: 0, startedAt: now, steps: [] }],
});

export const getOpenSegment = (
  activity: TurnActivity,
): ActivitySegment | undefined => {
  const lastSegment = activity.segments[activity.segments.length - 1];
  return lastSegment && lastSegment.endedAt === undefined
    ? lastSegment
    : undefined;
};

/** The tool the agent is waiting on right now, if any. */
export const getRunningStep = (
  segment: ActivitySegment,
): ActivityStep | undefined =>
  [...segment.steps]
    .reverse()
    .find(({ kind, status }) => kind === 'tool' && status === 'running');

const replaceLastSegment = (
  activity: TurnActivity,
  segment: ActivitySegment,
): TurnActivity => ({
  ...activity,
  segments: [...activity.segments.slice(0, -1), segment],
});

/** Adds a step to the open segment, or to a new one placed after the replies
 * so far if the work had stopped for a reply. */
const appendStep = (
  activity: TurnActivity,
  step: ActivityStep,
  { now, replyCount }: ActivityContext,
): TurnActivity => {
  const openSegment = getOpenSegment(activity);
  const segment = openSegment ?? {
    position: replyCount,
    startedAt: now,
    steps: [],
  };
  return {
    ...activity,
    segments: [
      ...(openSegment ? activity.segments.slice(0, -1) : activity.segments),
      { ...segment, steps: [...segment.steps, step] },
    ],
  };
};

const updateStep = (
  activity: TurnActivity,
  stepId: string,
  update: (step: ActivityStep) => ActivityStep,
): TurnActivity => ({
  ...activity,
  segments: activity.segments.map((segment) =>
    segment.steps.some(({ id }) => id === stepId)
      ? {
          ...segment,
          steps: segment.steps.map((step) =>
            step.id === stepId ? update(step) : step,
          ),
        }
      : segment,
  ),
});

const findStep = (
  activity: TurnActivity,
  predicate: (step: ActivityStep) => boolean,
): ActivityStep | undefined => {
  for (let index = activity.segments.length - 1; index >= 0; index--) {
    const { steps } = activity.segments[index];
    for (let stepIndex = steps.length - 1; stepIndex >= 0; stepIndex--) {
      if (predicate(steps[stepIndex])) {
        return steps[stepIndex];
      }
    }
  }
  return undefined;
};

/**
 * Ends the turn. A call still running never delivered its result -- whether
 * it finished, failed, or recovery gave up -- so it shows as stopped rather
 * than claiming a success nobody saw. Even a turn the server reports as
 * completed only says the turn ended, not that each call succeeded.
 */
const closeTurn = (
  activity: TurnActivity,
  now: number,
  status: 'done' | 'stopped',
): TurnActivity => ({
  ...activity,
  status,
  endedAt: now,
  segments: activity.segments.map((segment) => ({
    ...segment,
    endedAt: segment.endedAt ?? now,
    steps: segment.steps.map((step) =>
      step.status === 'running'
        ? { ...step, status: 'stopped', endedAt: now }
        : step,
    ),
  })),
});

/**
 * Applies one stream-derived action. Returns the same object when nothing
 * changed, so callers can skip a state update (and a re-render).
 */
export const reduceTurnActivity = (
  activity: TurnActivity,
  action: ActivityAction,
  context: ActivityContext,
): TurnActivity => {
  if (activity.status !== 'running') {
    return activity;
  }
  const { now } = context;

  switch (action.type) {
    case 'thinking': {
      // A model call right after a sub-agent's write-up is the supervisor
      // reading it back before it replies.
      const phase =
        activity.phase === 'drafting' || activity.phase === 'finalizing'
          ? 'finalizing'
          : 'thinking';
      return phase === activity.phase ? activity : { ...activity, phase };
    }

    case 'toolCalled': {
      if (isHandoffTool(action.toolName)) {
        return activity;
      }
      const input =
        action.input === undefined ? undefined : capActivityText(action.input);
      // The `tool_use` token announces a call before `tool_start` repeats it
      // with the arguments; without an id, pair them by tool name.
      const existing = action.id
        ? findStep(activity, ({ id }) => id === action.id)
        : findStep(
            activity,
            (step) =>
              step.status === 'running' &&
              step.toolName === action.toolName &&
              step.input === undefined,
          );
      if (existing) {
        return input === undefined
          ? activity
          : updateStep(activity, existing.id, (step) => ({ ...step, input }));
      }
      // Without an id, the step's position keeps two calls to the same tool
      // apart even when they land in the same millisecond.
      const stepCount = activity.segments.reduce(
        (count, { steps }) => count + steps.length,
        0,
      );
      return {
        ...appendStep(
          activity,
          {
            id: action.id ?? `${action.toolName}:${stepCount}`,
            kind: 'tool',
            toolName: action.toolName,
            input,
            status: 'running',
            startedAt: now,
          },
          context,
        ),
        phase: 'thinking',
      };
    }

    case 'toolFinished': {
      // Ids link a call to its result, and an id that matches nothing (e.g.
      // an ignored handoff's) must not claim some other call. Only results
      // without one, from older backends, fall back to the most recent
      // running call of the same tool.
      const step = action.id
        ? findStep(activity, ({ id }) => id === action.id)
        : findStep(
            activity,
            ({ kind, status, toolName }) =>
              kind === 'tool' &&
              status === 'running' &&
              (!action.toolName || toolName === action.toolName),
          );
      if (!step) {
        return activity;
      }
      return updateStep(activity, step.id, (current) => ({
        ...current,
        status: action.failed ? 'failed' : 'done',
        endedAt: now,
        output:
          action.output === undefined
            ? current.output
            : capActivityText(action.output),
        resultCount: action.failed
          ? undefined
          : countToolResults(current.toolName, action.output),
      }));
    }

    case 'drafting':
      return activity.phase === 'drafting' || !getOpenSegment(activity)
        ? activity
        : { ...activity, phase: 'drafting' };

    case 'narrated': {
      return {
        ...appendStep(
          activity,
          {
            id: action.id,
            kind: 'note',
            input: capActivityText(action.text.trim()),
            status: 'done',
            startedAt: now,
            endedAt: now,
          },
          context,
        ),
        phase: 'thinking',
      };
    }

    case 'replied': {
      const segment = getOpenSegment(activity);
      return segment
        ? replaceLastSegment(activity, { ...segment, endedAt: now })
        : activity;
    }

    case 'restarted':
      return {
        ...activity,
        phase: 'thinking',
        segments: [{ position: 0, startedAt: activity.startedAt, steps: [] }],
      };

    case 'finished':
      return closeTurn(activity, now, action.stopped ? 'stopped' : 'done');
  }
};

/**
 * A turn still marked running that is no longer streaming -- e.g. the page
 * reloaded mid-turn and the answer never came -- shows as stopped at the
 * last moment anything was seen happening.
 */
export const settleStaleActivity = (activity: TurnActivity): TurnActivity => {
  if (activity.status !== 'running') {
    return activity;
  }
  const lastSeenAt = Math.max(
    activity.startedAt,
    ...activity.segments.flatMap(({ startedAt, endedAt, steps }) => [
      startedAt,
      endedAt ?? startedAt,
      ...steps.map((step) => step.endedAt ?? step.startedAt),
    ]),
  );
  return closeTurn(activity, lastSeenAt, 'stopped');
};

const isSamePrompt = (serverPrompt: Message, localPrompt: Message) =>
  serverPrompt.content.trim() === localPrompt.content.trim();

/**
 * Pairs server prompts with the local prompts they came from, by text. The
 * lists can differ: Retry removes the failed prompt locally while the server
 * keeps both attempts, and a prompt that never reached the server is only
 * local. Lining up the longest common run of prompts handles both, and
 * walking back from the end pairs a prompt sent twice with its latest send.
 */
const matchLocalPrompts = (
  serverPrompts: Message[],
  localPrompts: Message[],
): Map<Message, Message> => {
  // sharedCounts[s][l]: prompts shared by the first s server prompts and the
  // first l local prompts.
  const sharedCounts = Array.from({ length: serverPrompts.length + 1 }, () =>
    Array.from({ length: localPrompts.length + 1 }, () => 0),
  );
  serverPrompts.forEach((serverPrompt, serverIndex) => {
    localPrompts.forEach((localPrompt, localIndex) => {
      sharedCounts[serverIndex + 1][localIndex + 1] = isSamePrompt(
        serverPrompt,
        localPrompt,
      )
        ? sharedCounts[serverIndex][localIndex] + 1
        : Math.max(
            sharedCounts[serverIndex][localIndex + 1],
            sharedCounts[serverIndex + 1][localIndex],
          );
    });
  });

  const matches = new Map<Message, Message>();
  let serverCount = serverPrompts.length;
  let localCount = localPrompts.length;
  while (serverCount > 0 && localCount > 0) {
    const serverPrompt = serverPrompts[serverCount - 1];
    const localPrompt = localPrompts[localCount - 1];
    if (isSamePrompt(serverPrompt, localPrompt)) {
      matches.set(serverPrompt, localPrompt);
      serverCount--;
      localCount--;
    } else if (
      sharedCounts[serverCount - 1][localCount] >=
      sharedCounts[serverCount][localCount - 1]
    ) {
      serverCount--;
    } else {
      localCount--;
    }
  }
  return matches;
};

/**
 * The server's history has no activity, so a restored conversation takes it
 * from the matching local prompt (see {@link matchLocalPrompts}). A turn
 * local storage still has as running either finished while polling
 * (`finalizeRunningAt` is when that was observed) or at some unknown time
 * before a reload, whose timings would be meaningless.
 */
export const carryOverActivity = (
  serverMessages: Message[],
  localMessages: Message[],
  finalizeRunningAt?: number,
): Message[] => {
  const localPromptFor = matchLocalPrompts(
    serverMessages.filter(({ role }) => role === 'user'),
    localMessages.filter(({ role }) => role === 'user'),
  );
  return serverMessages.map((message) => {
    const activity = localPromptFor.get(message)?.activity;
    if (!activity) {
      return message;
    }
    if (activity.status !== 'running') {
      return { ...message, activity };
    }
    return finalizeRunningAt === undefined
      ? message
      : {
          ...message,
          activity: closeTurn(activity, finalizeRunningAt, 'done'),
        };
  });
};
